/**
 * @file BenefitPlanFilesTab.js
 * @description Tab component for managing benefit plan-related files.
 */

import { useState, useEffect } from 'react';
import PlanFilesManager from './PlanFilesManager';
import VideoThumbnail from '../../ui/VideoThumbnail';
import { getFileIcon } from '../../ui/svg/FileIcons';
import { getFileCategory } from '../../helpers/FileUtils';
import styles from '../PlanYear.module.scss';
import { getText } from '../../../i18n';

const BenefitPlanFilesTab = ({ data, onChange, onSave }) => {
    const [activeActionMenuId, setActiveActionMenuId] = useState(null);

    useEffect(() => {
        const handleClick = () => {
            setActiveActionMenuId(null);
        };

        document.addEventListener('click', handleClick);
        return () => {
            document.removeEventListener('click', handleClick);
        };
    }, []);

    const isVideoFile = fileType => getFileCategory(fileType) === 'video';
    const isImageFile = fileType => getFileCategory(fileType) === 'image';

    const renderFileThumbnail = assetFile => {
        if (isVideoFile(assetFile.fileType)) {
            return (
                <VideoThumbnail
                    videoUrl={assetFile.url}
                    avatarURL={assetFile.avatarURL}
                    width={80}
                    height={64}
                    alt={assetFile.name}
                />
            );
        }

        if (isImageFile(assetFile.fileType) && assetFile.url) {
            return <img src={assetFile.url} alt={assetFile.name} />;
        }

        return getFileIcon(assetFile.fileType);
    };

    const renderFileRow = (
        assetFile,
        index,
        { onDelete, onPreview, onEdit, onDownload, canPreview },
    ) => {
        const isActionMenuOpen = activeActionMenuId === assetFile.id;

        return (
            <div
                key={assetFile.id}
                className={`${styles.fileBox} ${styles.fileRowAligned}`}
            >
                <div className={styles.fileThumbnail}>
                    {renderFileThumbnail(assetFile)}
                </div>

                <div className={styles.fileInfo}>
                    <div className={styles.fileName}>{assetFile.name}</div>
                    <div className={styles.fileType}>
                        {assetFile.fileType?.toUpperCase()}
                    </div>
                </div>

                <div className={styles.fileActions}>
                    <button
                        type="button"
                        className={styles.actionIconDelete}
                        onClick={() => onDelete(assetFile)}
                        title={getText('msg_b9ea4b3f01ac')}
                    >
                        <i className="bi bi-trash3"></i>
                    </button>

                    <div className={styles.actionMenuWrapper}>
                        <button
                            type="button"
                            className={styles.actionIconPreview}
                            onClick={event => {
                                event.stopPropagation();
                                setActiveActionMenuId(prev =>
                                    prev === assetFile.id ? null : assetFile.id,
                                );
                            }}
                            title={getText('msg_9e1b94c56613')}
                        >
                            <i className="bi bi-eye"></i>
                        </button>
                        {isActionMenuOpen && (
                            <div
                                className={styles.actionMenu}
                                onClick={event => event.stopPropagation()}
                            >
                                <ul>
                                    {canPreview(assetFile) && (
                                        <li
                                            onClick={() => {
                                                onPreview(index);
                                                setActiveActionMenuId(null);
                                            }}
                                        >
                                            {getText('msg_f1fbb2b43dca')}
                                        </li>
                                    )}
                                    <li
                                        onClick={() => {
                                            onDownload(assetFile);
                                            setActiveActionMenuId(null);
                                        }}
                                    >
                                        {getText('msg_a479c9c34e87')}
                                    </li>
                                </ul>
                            </div>
                        )}
                    </div>

                    <button
                        type="button"
                        className={styles.actionIconEdit}
                        onClick={() => onEdit(assetFile)}
                        title={getText('msg_43b9013be021')}
                    >
                        <i className="bi bi-pencil"></i>
                    </button>
                </div>
            </div>
        );
    };

    const getDisplayFileName = currentFile => {
        const fullFileName = `${currentFile.name}${currentFile.fileType ? `.${currentFile.fileType}` : ''}`;

        if (fullFileName.length <= 80) {
            return fullFileName;
        }

        return `${fullFileName.slice(0, 80)}...`;
    };

    const getFullFileName = currentFile =>
        `${currentFile.name}${currentFile.fileType ? `.${currentFile.fileType}` : ''}`;

    return (
        <PlanFilesManager
            data={data}
            onChange={onChange}
            storageKey="planAssets"
            entityLabel={getText('msg_307be38eef5c')}
            headerTitle={getText('msg_20e338624cee')}
            headerDescription={getText('msg_262eb0430031')}
            logContext={getText('msg_48f94acb987e')}
            renderFileRow={renderFileRow}
            showPopulationGroups={false}
            showNameAndTagsFields={true}
        />
    );
};

export default BenefitPlanFilesTab;
